import { useMemo } from 'react'
import links from 'links'
import { useFt } from 'hooks'
import { constants } from 'helpers'

import { useUser } from 'modules/user'
import { usePrivateSale } from 'modules/products'
import { useFeaturedBrandNavigationBanners } from 'modules/brands'
import { useCandleSubscription, useCaseSubscription, useSubscription } from 'modules/subscription'
import { ExclusiveLabel } from 'components/dataDisplay'

import TheEditBanner from 'compositions/banners/TheEditBanner/TheEditBanner'
import { navMessages as messages } from 'compositions/navigation'

import {
  BuyQueueBanner,
  CasesBanner,
  CaseSubscriptionBanner,
  CandleSubscriptionBanner,
  GiftSubscriptionBanner,
  DynamicNavigationBanner,
  FeaturedBrandBanner,
  SaksCollaborationBanner,
} from '../components/banners'


const useDesktopNav = () => {
  const isTheEditEnabled = useFt(constants.features.theEdit)
  const isTheShopEnabled = useFt(constants.features.theShop)
  const isCandleSubscriptionEnabled = useFt(constants.features.candleSubscription)
  const isFragranceFamiliesPageEnabled = useFt(constants.features.fragranceFamiliesPage)
  const isNotesLibraryPageEnabled = useFt(constants.features.noteLibPages)
  const isGiftSetsPageEnabled = useFt(constants.features.giftSetsPage)
  const isSaksCollaborationEnabled = useFt(constants.features.collaborationSaks)
  const isGiftCardFraudEnabled = useFt(constants.features.giftCardFraud)
  const isHolidayEnabled = useFt(constants.features.holiday)


  const { gender, hasScentProfile } = useUser()

  const featuredBrandNavigationBanners = useFeaturedBrandNavigationBanners()
  const isFeaturedBrandPerfumesBannerExists = Boolean(featuredBrandNavigationBanners?.perfumes)
  const isFeaturedBrandColognesBannerExists = Boolean(featuredBrandNavigationBanners?.colognes)
  const isFeaturedBrandDiscoverBannerExists = Boolean(featuredBrandNavigationBanners?.discover)

  const { privateSale } = usePrivateSale()
  const isPrivateSaleEnabled = Boolean(privateSale)

  const { subscription } = useSubscription()
  const isSubscribed = subscription?.isSubscribed
  const isBuyQueueEnabled = subscription?.stateOptions?.canBuyQueue
  const canApplyTossInOffer = subscription?.canApplyTossInOffer

  const { caseSubscription } = useCaseSubscription()
  const isCaseSubscriptionEnabled = caseSubscription?.isEnabled
  const isCaseSubscriptionSelected = caseSubscription?.isSelected

  const { candleSubscription } = useCandleSubscription()
  const isCandleSubscriptionSelected = candleSubscription?.isSelected
  const isCandleSubscriptionVisible = Boolean(isCandleSubscriptionEnabled && candleSubscription?.isEnabled)

  const isTopLevelCandleClubEntryPointVisible = isCandleSubscriptionVisible && !isCandleSubscriptionSelected

  const discoverSectionBanner = isFeaturedBrandDiscoverBannerExists ? FeaturedBrandBanner : DynamicNavigationBanner
  const perfumesSectionBanner = isFeaturedBrandPerfumesBannerExists ? FeaturedBrandBanner : DynamicNavigationBanner
  const colognesSectionBanner = isFeaturedBrandColognesBannerExists ? FeaturedBrandBanner : DynamicNavigationBanner

  return useMemo(() => {
    const data: Navigation.HeaderDesktopSection[] = [
      {
        id: 'perfumes',
        title: messages.perfumes,
        list: [
          {
            id: 'perfumes-all',
            title: messages.allPerfumes,
            to: links.subscription.perfumes,
          },
          {
            id: 'perfumes-new-arrivals',
            title: messages.newArrivals,
            to: links.newArrivals.perfumes,
          },
          {
            id: 'perfumes-bestsellers',
            title: messages.bestsellers,
            to: links.bestsellers.perfumes,
          },
          {
            id: 'perfumes-pom',
            title: messages.pom,
            to: links.subscription.currentPerfumeOfTheMonth,
          },
          {
            id: 'perfumes-clean',
            title: messages.clean,
            to: links.clean.perfumes,
          },
          {
            id: 'perfumes-exclusive',
            title: messages.exclusive,
            to: `${links.subscription.perfumes}?category=exclusive`,
          },
          {
            id: 'perfumes-collections',
            title: messages.collections,
            to: links.collectionsWomen,
          },
        ],
        withColumns: true,
        bannerComponent: perfumesSectionBanner,
      },
      {
        id: 'colognes',
        title: messages.colognes,
        list: [
          {
            id: 'colognes-all',
            title: messages.allColognes,
            to: links.subscription.colognes,
          },
          {
            id: 'colognes-new-arrivals',
            title: messages.newArrivals,
            to: links.newArrivals.colognes,
          },
          {
            id: 'colognes-bestsellers',
            title: messages.bestsellers,
            to: links.bestsellers.colognes,
          },
          {
            id: 'colognes-com',
            title: messages.com,
            to: links.subscription.currentCologneOfTheMonth,
          },
          {
            id: 'colognes-clean',
            title: messages.clean,
            to: links.clean.colognes,
          },
          {
            id: 'colognes-exclusive',
            title: messages.exclusive,
            to: `${links.subscription.colognes}?category=exclusive`,
          },
          {
            id: 'colognes-collections',
            title: messages.collections,
            to: links.collectionsMen,
          },
        ],
        withColumns: true,
        bannerComponent: colognesSectionBanner,
      },
      {
        id: 'beauty',
        title: messages.beauty,
        to: links.subscription.beauty,
      },
      isTopLevelCandleClubEntryPointVisible && {
        id: 'candleClub',
        title: messages.candleSubscription,
        to: links.candleSubscription,
      },
      { isDivider: true },
      {
        id: 'shop',
        title: messages.shop,
        list: [
          isTheEditEnabled && {
            id: 'header-shop-edit',
            title: messages.edit,
            to: links.shop.theEdit,
          },
          {
            id: 'header-shop-scentbirdCases',
            title: messages.scentbirdCases,
            to: links.shop.scentbirdCases,
          },
          isTheShopEnabled && {
            id: 'header-shop-fullSizeFragrances',
            title: messages.fullSizeFragrances,
            to: gender === 'FEMALE' ? links.shop.perfumes : links.shop.colognes,
          },
          {
            id: 'header-shop-giftsets',
            title: messages.giftSetsPage,
            to: gender === 'MALE' ? links.giftSets.colognes : links.giftSets.perfumes,
          },
          isTheShopEnabled && {
            id: 'header-shop-candlesHomeScents',
            title: messages.candlesHomeScents,
            to: links.shop.candlesHomeScents,
          },
          isTheShopEnabled && {
            id: 'header-shop-bathBody',
            title: messages.bathBody,
            to: links.shop.bathBody,
          },
          {
            id: 'header-shop-accessories',
            title: messages.accessories,
            to: links.shop.accessories,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        withColumns: true,
        bannerComponent: isTheEditEnabled ? TheEditBanner : CasesBanner,
      },
      {
        id: 'discover',
        title: messages.discover,
        list: [
          {
            id: 'header-discover-scentProfile',
            title: messages.scentProfile,
            to: hasScentProfile ? links.scentProfile : links.smartRecommendations,
          },
          isSaksCollaborationEnabled && {
            id: 'header-discover-saks-collaboration',
            title: messages.saksCollaboration,
            to: links.saks,
            label: messages.labels.new,
          },
          {
            id: 'header-discover-allBrands',
            title: messages.allBrands,
            to: links.brands,
          },
          {
            id: 'header-discover-fomArchive',
            title: messages.fomArchive,
            to: links.subscription.perfumeOfTheMonthList,
          },
          isNotesLibraryPageEnabled && {
            id: 'header-discover-notesLibrary',
            title: messages.notesLibrary,
            to: links.notes,
          },
          isFragranceFamiliesPageEnabled && {
            id: 'header-discover-fragranceFamilies',
            title: messages.fragranceFamilies,
            to: links.fragranceFamilies,
          },
          {
            id: 'header-discover-scentBlog',
            title: messages.scentBlog,
            toTab: links.blog,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        withColumns: true,
        bannerComponent: isSaksCollaborationEnabled ? SaksCollaborationBanner : discoverSectionBanner,
      },
      isSubscribed && {
        id: 'member perks',
        title: messages.memberPerks,
        list: [
          isBuyQueueEnabled && {
            id: 'header-member-perks-buyYourQueue',
            title: messages.buyYourQueue,
            to: links.buyQueue,
          },
          isPrivateSaleEnabled && {
            id: 'header-member-perks-privateSale',
            title: messages.privateSale,
            to: links.privateSale,
          },
          {
            id: 'header-member-perks-referralProgram',
            title: messages.referralProgram,
            to: links.invite,
          },
          canApplyTossInOffer && {
            id: 'header-member-perks-lastMinuteAddons',
            title: messages.lastMinuteAddons,
            to: links.tossIn,
          },
          isCaseSubscriptionEnabled && {
            id: 'header-member-perks-caseSubscription',
            title: messages.caseSubscription,
            to: links.caseSubscription,
          },
          isCandleSubscriptionVisible && {
            id: 'header-member-perks-candleSubscription',
            title: messages.candleSubscription,
            to: links.candleSubscription,
            label: isCandleSubscriptionSelected ? messages.labels.subscribed : messages.labels.new,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        withColumns: false,
        bannerComponent: ({ onClick }) => {
          if (isCandleSubscriptionVisible && !isCandleSubscriptionSelected) {
            return (
              <CandleSubscriptionBanner onClick={onClick} />
            )
          }

          if (isCaseSubscriptionEnabled && !isCaseSubscriptionSelected) {
            return (
              <CaseSubscriptionBanner onClick={onClick} />
            )
          }

          return <BuyQueueBanner onClick={onClick} />
        },
      },
      {
        id: 'gifts',
        title: messages.gifts,
        list: [
          {
            id: 'header-gifts-subscription',
            title: messages.giftSubscription,
            to: links.giftSubscription,
          },
          !isGiftCardFraudEnabled && {
            id: 'header-gifts-card',
            title: messages.giftCard,
            to: links.giftCard,
          },
          isGiftSetsPageEnabled && {
            id: 'header-gift-sets',
            title: messages.giftSetsPage,
            to: links.giftSets.root,
          },
          isHolidayEnabled && {
            id: 'header-gift-hub',
            title: messages.holidayGifts,
            to: links.gifts,
            exclusiveLabel: <ExclusiveLabel labelText={messages.exclusives} />,
          },
          !isHolidayEnabled && {
            id: 'header-gifts',
            title: messages.allGifts,
            to: links.gifts,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        background: isHolidayEnabled,
        withColumns: false,
        bannerComponent: GiftSubscriptionBanner,
      },
    ]

    return data.filter(Boolean)
  }, [
    gender,
    hasScentProfile,
    discoverSectionBanner,
    isTheEditEnabled,
    isBuyQueueEnabled,
    canApplyTossInOffer,
    isCandleSubscriptionVisible,
    isCandleSubscriptionSelected,
    isCaseSubscriptionEnabled,
    isCaseSubscriptionSelected,
    isFragranceFamiliesPageEnabled,
    isNotesLibraryPageEnabled,
    isSubscribed,
    isTheShopEnabled,
    isPrivateSaleEnabled,
    isGiftSetsPageEnabled,
    isTopLevelCandleClubEntryPointVisible,
    isSaksCollaborationEnabled,
    isGiftCardFraudEnabled,
    perfumesSectionBanner,
    colognesSectionBanner,
  ])
}


export default useDesktopNav
